'use client';

import { Language } from '@coolblue-development/constants';
import { Box, Flex, Typography } from '@coolblue-development/freddy';
import selectOrderTypeTrigger from '@coolblue-development/ga4-events/triggers/OrderBlock/selectOrderTypeTrigger';
import showOrderTypeTrigger from '@coolblue-development/ga4-events/triggers/OrderBlock/showOrderTypeTrigger';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import { SelectableBox, SelectableBoxGroup } from '@coolblue-development/next-patterns/client';
import { SalesPrice, SalesPriceCurrent } from '@coolblue-development/next-sales-price/client';
import { Product } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { ReactElement } from 'react';

const urlMap = {
  [Language.DUTCH]: {
    oneTime: 'product',
    subscription: 'abonnement'
  },
  [Language.FRENCH]: {
    oneTime: 'produit',
    subscription: 'abonnement'
  },
  [Language.GERMAN]: {
    oneTime: 'produkt',
    subscription: 'abonnement'
  },
  [Language.ENGLISH]: {
    oneTime: 'product',
    subscription: 'subscription'
  }
};

export const SubscriptionSwitcher = ({
  subscription,
  salesPrice,
  language
}: {
  subscription: Product['subscription'];
  salesPrice: Product['salesPrice'];
  language: Language;
}): ReactElement => {
  const { t } = useTranslation('webshop.subscriptionSwitcher');
  const { trackEvent } = useAnalytics();

  if (!subscription || !subscription.installmentAmount) return <></>;

  let location;
  if (typeof window !== 'undefined') {
    location = window.location;
  }

  const isSubscriptionPage = location?.pathname.includes('/' + urlMap[language].subscription);

  const redirectUrl = location?.href.replace(
    isSubscriptionPage ? '/' + urlMap[language].subscription : '/' + urlMap[language].oneTime,
    isSubscriptionPage ? '/' + urlMap[language].oneTime : '/' + urlMap[language].subscription
  );

  const oneTimeText =
    language === Language.DUTCH
      ? 'oneTimeDutch'
      : language === Language.FRENCH
        ? 'oneTimeFrench'
        : 'oneTime';

  return (
    <Box>
      <Typography variant='h5'>{t('title')}</Typography>
      <SelectableBoxGroup
        inputPlacement='hidden'
        name='subscriptionSwitcher'
        spacing='compact'
        type='radio'
        value={isSubscriptionPage ? 'subscription' : 'oneTime'}
      >
        <Flex gap={200}>
          <SelectableBox
            value='oneTime'
            onClick={() => {
              if (typeof window !== 'undefined' && redirectUrl && isSubscriptionPage) {
                window.location.href = redirectUrl;
              }

              trackEvent(
                selectOrderTypeTrigger({
                  content: 'one-time'
                })
              );
            }}
          >
            <Flex gap={200}>
              {t(oneTimeText)}
              <Box hidden={!isSubscriptionPage}>
                <SalesPrice
                  size='small'
                  variant='subscription'
                  currentPrice={<SalesPriceCurrent price={salesPrice.includingVat} />}
                />
              </Box>
            </Flex>
          </SelectableBox>
          <SelectableBox
            value='subscription'
            onClick={() => {
              if (typeof window !== 'undefined' && redirectUrl && !isSubscriptionPage) {
                window.location.href = redirectUrl;
              }

              trackEvent(
                selectOrderTypeTrigger({
                  content: 'subscription'
                })
              );
            }}
          >
            <Flex gap={200}>
              {t('subscription')}
              {!isSubscriptionPage && (
                <SalesPrice
                  size='small'
                  variant='subscription'
                  currentPrice={<SalesPriceCurrent price={subscription.installmentAmount} />}
                />
              )}
            </Flex>
          </SelectableBox>
        </Flex>
      </SelectableBoxGroup>
      <TrackImmediate trigger={showOrderTypeTrigger()} />
    </Box>
  );
};
